import {HourlyWeatherForecastItem} from '../../api/types';
import {dayStart, now as clockNow} from '../Dates';
import {dayRange, DayRange} from './dayRange';

/** Hours of pressure history the trend arrow is computed over. */
export const PRESSURE_TREND_HOURS = 3;
/** Below this change the pressure is reported as steady, per WMO practice. */
export const PRESSURE_TREND_THRESHOLD_HPA = 1;

export type PressureTrend = 'up' | 'down' | 'stable';

const isToday = (timestamp: number, timezone: string, now: number) =>
  dayStart(timestamp, timezone) === dayStart(now, timezone);

/**
 * The hourly row a "Current conditions" card shows: the row of the place's
 * civil day nearest `now`.
 *
 * Seeded with the first row, which is only ever replaced by a row of today --
 * so a forecast with nothing for today falls back to its first row rather than
 * to nothing. That is the behaviour the mobile card has always had; callers
 * that need a day to range over get null from `currentConditions` instead.
 */
export const currentHourRow = (
  hourly: HourlyWeatherForecastItem[],
  timezone: string,
  now: number = clockNow(),
): HourlyWeatherForecastItem | null =>
  hourly.reduce<HourlyWeatherForecastItem | null>((previous, item) => {
    if (
      previous === null ||
      (isToday(item.timestamp, timezone, now) &&
        Math.abs(now - item.timestamp) < Math.abs(now - previous.timestamp))
    ) {
      return item;
    }
    return previous;
  }, null);

/** The rows of the place's civil day, in the order served. */
export const todayRows = (
  hourly: HourlyWeatherForecastItem[],
  timezone: string,
  now: number = clockNow(),
): HourlyWeatherForecastItem[] =>
  hourly.filter((item) => isToday(item.timestamp, timezone, now));

/** Rain over `today` up to and including the current hour; a missing amount counts as none. */
export const rainSoFarToday = (
  today: HourlyWeatherForecastItem[],
  current: HourlyWeatherForecastItem,
): number =>
  today
    .filter((item) => item.timestamp <= current.timestamp)
    .reduce((sum, item) => sum + (item.RR || 0), 0);

/**
 * Sea-level pressure change over the last three hours, or undefined without a
 * full three-hour window.
 *
 * The oldest row in the window is found by timestamp rather than taken as
 * `[0]`: `hourly` comes straight from the API and nothing sorts it, so array
 * order would silently invert the arrow on a descending payload. And the
 * window must be complete, not merely hold two readings: just after local
 * midnight a forecast starting at 00:00 would otherwise compare a one-hour
 * change against a threshold calibrated for three.
 */
export const pressureTrend = (
  hourly: HourlyWeatherForecastItem[],
  current: HourlyWeatherForecastItem,
): PressureTrend | undefined => {
  const since = current.timestamp - PRESSURE_TREND_HOURS * 3600;
  const earliest = hourly.reduce<HourlyWeatherForecastItem | null>(
    (oldest, item) => {
      if (item.timestamp < since || item.timestamp > current.timestamp) {
        return oldest;
      }
      return oldest === null || item.timestamp < oldest.timestamp
        ? item
        : oldest;
    },
    null,
  );
  if (earliest === null || earliest.timestamp > since) {
    return undefined;
  }
  const delta = current.SLP - earliest.SLP;
  if (delta > PRESSURE_TREND_THRESHOLD_HPA) {
    return 'up';
  }
  if (delta < -PRESSURE_TREND_THRESHOLD_HPA) {
    return 'down';
  }
  return 'stable';
};

export type CurrentConditions = {
  /** The row the card shows. */
  current: HourlyWeatherForecastItem;
  /** The place's civil day. */
  today: HourlyWeatherForecastItem[];
  range: DayRange;
  rainSoFar: number;
  pressureTrend: PressureTrend | undefined;
};

/**
 * Everything a place's "Current conditions" card derives from its hourly
 * forecast, or null when there is nothing to show: no rows, or no row of
 * today to range over.
 */
export const currentConditions = (
  hourly: HourlyWeatherForecastItem[],
  timezone: string,
  now: number = clockNow(),
): CurrentConditions | null => {
  const current = currentHourRow(hourly, timezone, now);
  if (!current) {
    return null;
  }
  const today = todayRows(hourly, timezone, now);
  const range = dayRange(today, current);
  if (!range) {
    return null;
  }
  return {
    current,
    today,
    range,
    rainSoFar: rainSoFarToday(today, current),
    pressureTrend: pressureTrend(hourly, current),
  };
};
